import { useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { TFunction } from "i18next";
import { Api } from "services/api/api.service";
import type { CompanyGig } from "model/Gig";
import { ApplicationResolution, ApplicationStatus } from "model/Application";
import type { TeamPublicApplicationModalState } from "./teamPublicDetailsModal.contract";

interface UseTeamPublicDetailsActionsParams {
  companyId?: number;
  gigData: CompanyGig | null;
  setGigData: Dispatch<SetStateAction<CompanyGig | null>>;
  setSaveErrorText: (value: string | null) => void;
  t: TFunction;
}

interface UseTeamPublicDetailsActionsResult {
  activeApplicationId: number | null;
  isApplicationActionLoading: boolean;
  applicationModalState: TeamPublicApplicationModalState;
  handleOpenApplicationActionConfirm: (
    applicationId: number,
    status: ApplicationStatus.OFFERED | ApplicationStatus.CLOSED,
    applicantName: string,
  ) => void;
  handleCloseApplicationModal: () => void;
  handleConfirmApplicationAction: () => Promise<void>;
}

export const useTeamPublicDetailsActions = (
  params: UseTeamPublicDetailsActionsParams,
): UseTeamPublicDetailsActionsResult => {
  const [activeApplicationId, setActiveApplicationId] = useState<number | null>(
    null,
  );
  const [applicationModalState, setApplicationModalState] =
    useState<TeamPublicApplicationModalState>({ type: "closed" });
  const [isApplicationActionLoading, setIsApplicationActionLoading] =
    useState(false);
  const isApplicationActionInFlightRef = useRef(false);

  const handleApplicationStatusChange = async (
    applicationId: number,
    status: ApplicationStatus.OFFERED | ApplicationStatus.CLOSED,
  ): Promise<boolean> => {
    if (
      !params.companyId ||
      activeApplicationId != null ||
      isApplicationActionInFlightRef.current
    ) {
      return false;
    }
    const api = Api();
    isApplicationActionInFlightRef.current = true;
    setActiveApplicationId(applicationId);
    try {
      const applicationResponse =
        await api.company.application.getApplicationById(
          params.companyId,
          applicationId,
        );
      const payload = {
        ...applicationResponse.data,
        status,
        ...(status === ApplicationStatus.CLOSED
          ? { resolution: ApplicationResolution.REJECTED }
          : {}),
      };
      await api.company.application.updateApplicationStatus(
        params.companyId,
        applicationId,
        payload,
      );
      params.setGigData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          applications: prev.applications.map((application) =>
            application.id === applicationId
              ? {
                  ...application,
                  status,
                  resolution:
                    status === ApplicationStatus.CLOSED
                      ? ApplicationResolution.REJECTED
                      : application.resolution,
                }
              : application,
          ),
        };
      });
      return true;
    } catch {
      setApplicationModalState({ type: "error" });
      return false;
    } finally {
      isApplicationActionInFlightRef.current = false;
      setActiveApplicationId(null);
    }
  };

  const handleOpenApplicationActionConfirm = (
    applicationId: number,
    status: ApplicationStatus.OFFERED | ApplicationStatus.CLOSED,
    applicantName: string,
  ): void => {
    setApplicationModalState({
      type: "confirm",
      action: { applicationId, status, applicantName },
    });
  };

  const handleCloseApplicationModal = (): void => {
    if (isApplicationActionLoading) return;
    setApplicationModalState({ type: "closed" });
  };

  const handleConfirmApplicationAction = async (): Promise<void> => {
    if (
      applicationModalState.type !== "confirm" ||
      isApplicationActionLoading
    ) {
      return;
    }
    setIsApplicationActionLoading(true);
    const isSuccess = await handleApplicationStatusChange(
      applicationModalState.action.applicationId,
      applicationModalState.action.status,
    );
    setIsApplicationActionLoading(false);
    if (isSuccess) {
      setApplicationModalState({ type: "closed" });
    }
  };

  return {
    activeApplicationId,
    isApplicationActionLoading,
    applicationModalState,
    handleOpenApplicationActionConfirm,
    handleCloseApplicationModal,
    handleConfirmApplicationAction,
  };
};
